import React, { useState, useEffect } from 'react'
import {
  Grid,
  List,
  Card,
  CardHeader,
  CardContent,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Button,
  Divider,
} from '@mui/material'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import { useInput, useTranslate } from 'react-admin';

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1)
}

function intersection(a, b) {
  return a.filter((value) => b.indexOf(value) !== -1)
}

function union(a, b) {
  return [...a, ...not(b, a)]
}

export const TransferList = ({ source, options, selected, labelField = 'description', ...props }) => {
  const translate = useTranslate()
  const [checked, setChecked] = useState([])
  const [left, setLeft] = useState(options || [])
  const [right, setRight] = useState(selected || [])
  const { field } = useInput({source, defaultValue : selected || [] });

  useEffect(() => {
    if (!selected) {
      field.onChange([])
      setChecked([])
      setRight([])
      setLeft(options)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options])

  const leftChecked = intersection(checked, left)
  const rightChecked = intersection(checked, right)

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value)
    const newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    setChecked(newChecked)
  }

  const numberOfChecked = (items) => intersection(checked, items).length

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items))
    } else {
      setChecked(union(checked, items))
    }
  }

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked))
    field.onChange(right.concat(leftChecked))
    setLeft(not(left, leftChecked))
    setChecked(not(checked, leftChecked))
  }

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked))
    setRight(not(right, rightChecked))
    field.onChange(not(right, rightChecked))
    setChecked(not(checked, rightChecked))
  }

  const customList = (title, items) => (
    <Card className='transferListCard'>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
            disabled={items.length === 0}
            inputProps={{
              'aria-label': 'all items selected',
            }}
          />
        }
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} ${translate('ra.field.selected')}`}
      />
      <Divider />
      <CardContent>
        <List dense component='div' role='list' className='cardList'>
          {items.filter(elem => elem).map((value) => {
            const labelId = `transfer-list-all-item-${value}-label`
            return (
              <ListItem key={value.id} role='listitem' button onClick={handleToggle(value)}>
                <ListItemIcon>
                  <Checkbox
                    checked={checked.indexOf(value) !== -1}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={value?.[labelField]} />
              </ListItem>
            )
          })}
          <ListItem />
        </List>
      </CardContent>
    </Card>
  )

  return (
    <Grid container spacing={2} justifyContent='center' alignItems='center' className='transferListComponent'>
      <Grid item xs={5}>
        {customList(translate('ra.field.choices'), left)}
      </Grid>
      <Grid item xs={2}>
        <Grid container direction='column' alignItems='center'>
          <Button
            variant='outlined'
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label='move selected right'
          >
            <KeyboardArrowRight />
          </Button>
          <Button
            variant='outlined'
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label='move selected left'
          >
            <KeyboardArrowLeft />
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={5}>
        {customList(translate('ra.field.chosen'), right)}
      </Grid>
    </Grid>
  )
}
